import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) nav("/", { replace: true });
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20"
             style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #3B82F6 0, transparent 40%), radial-gradient(circle at 80% 70%, #6366F1 0, transparent 40%)" }} />
        <div className="relative flex items-center gap-3">
          <img src="/simplex-logo.png" alt="Simplex" className="h-10 w-auto invert" data-testid="login-hero-logo" />
        </div>
        <div className="relative space-y-4">
          <h1 className="font-heading text-4xl font-bold leading-tight">
            Collect what you're owed, on time.
          </h1>
          <p className="text-slate-300 text-base leading-relaxed max-w-md">
            Track domestic and export receivables, spot ageing risk instantly, and send
            automatic weekly payment reminders — all in one workspace.
          </p>
        </div>
        <div className="relative text-slate-400 text-sm">Receivables & Reminder Management</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm pt-fade-up">
          <div className="lg:hidden flex items-center mb-8">
            <img src="/simplex-logo.png" alt="Simplex" className="h-9 w-auto dark:invert" data-testid="login-mobile-logo" />
          </div>
          <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-1" data-testid="login-brand-title">
            Simplex Receivables Trackers
          </p>
          <h2 className="font-heading text-2xl font-semibold tracking-tight">Sign in</h2>
          <p className="text-sm text-muted-foreground mt-1 mb-6">
            Enter your credentials to access the dashboard.
          </p>          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} required
                     onChange={(e) => setEmail(e.target.value)}
                     placeholder="you@company.com" data-testid="login-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} required
                     onChange={(e) => setPassword(e.target.value)}
                     placeholder="••••••••" data-testid="login-password-input" />
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
